import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Moon, Sun } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { UserAvatar } from '../ui/user-avatar';
import { NotificationMenu } from '../notifications/NotificationMenu';

interface UserInfoSectionProps {
  displayName?: string;
  className?: string;
  isCollapsed?: boolean;
}

/**
 * A reusable component for displaying user information in the sidebar of app layouts
 * Includes user name, avatar, logout button, and dark mode toggle
 */
export const UserInfoSection: React.FC<UserInfoSectionProps> = ({ 
  displayName,
  className = '',
  isCollapsed = false
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <div className={`border-t dark:border-gray-700 p-4 mt-auto bg-white dark:bg-gray-800 ${className}`}>
      <div className="flex flex-col gap-4">
        {!isCollapsed && displayName && (
          <div className="text-center">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {displayName}
            </span>
          </div>
        )}
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-4">
            <div>
              <NotificationMenu>
                <UserAvatar size="sm" className="h-8 w-8" />
              </NotificationMenu>
            </div>
            <div>
              <button 
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-center gap-2">
              <button 
                onClick={() => {
                  logout();
                  navigate('/');
                }}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title={isDarkMode ? "Light Mode" : "Dark Mode"}
              >
                {isDarkMode ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </button>
              <NotificationMenu>
                <UserAvatar size="sm" className="h-8 w-8" />
              </NotificationMenu>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserInfoSection;
